<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChatMessage, ChatSender } from '@/services/chat/chatHttp.service'
import ResourceImage from '@/components/resource/resourceImage.vue'
import MessageEmbed from '@/components/messaging/MessageEmbed.vue'
import MessagePoll from '@/components/messaging/MessagePoll.vue'
import MessageAttachment from '@/components/messaging/MessageAttachment.vue'
import { useUserStore } from '@/config/store/userStore'
import MaterialSymbolsReplyRounded from '~icons/material-symbols/reply-rounded'
import MaterialSymbolsEditOutlineRounded from '~icons/material-symbols/edit-outline-rounded'
import MaterialSymbolsDeleteOutlineRounded from '~icons/material-symbols/delete-outline-rounded'
import MaterialSymbolsAddReactionOutlineRounded from '~icons/material-symbols/add-reaction-outline-rounded'
import MaterialSymbolsDoneAllRounded from '~icons/material-symbols/done-all-rounded'
import MaterialSymbolsCheckRounded from '~icons/material-symbols/check-rounded'
import { displayLocale } from '@/services/application/displayLocale'

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉']
const URL_PATTERN = /(https?:\/\/[^\s<]+)/gi
const MENTION_TOKEN = /\[\[BC_MENTION:(\d+)\]\]/g

type BodySegment =
  | { kind: 'html'; html: string }
  | {
      kind: 'mention'
      id: string
      label: string
      isMe: boolean
      resource: Pick<ChatSender, '_id' | 'firstname' | 'lastname' | 'picture'>
    }

const { t } = useI18n()
const userStore = useUserStore()

const props = defineProps<{
  message: ChatMessage
  compact?: boolean
}>()

const emits = defineEmits<{
  (e: 'reply', message: ChatMessage): void
  (e: 'react', message: ChatMessage, emoji: string): void
  (e: 'edit', message: ChatMessage): void
  (e: 'delete', message: ChatMessage): void
  (e: 'updated', message: ChatMessage): void
}>()

const showReactionPicker = ref(false)

const myResourceId = computed(() => String(userStore.user?._id || ''))
const isMine = computed(() => String(props.message.senderId) === myResourceId.value)
const isMentioned = computed(() =>
  (props.message.mentionIds || []).some(id => String(id) === myResourceId.value),
)

const seenBy = computed(() => props.message.seenBy || [])
const isSeen = computed(() => isMine.value && seenBy.value.length > 0)
const seenLabel = computed(() => {
  if (!isSeen.value) return t('modules.chat.sent')
  if (seenBy.value.length === 1) {
    const person = seenBy.value[0]
    const name = `${person.firstname || ''} ${person.lastname || ''}`.trim()
    if (!name) return t('modules.chat.seen')
    return t('modules.chat.seenByName', { name })
  }
  return t('modules.chat.seenByCount', { count: seenBy.value.length })
})

const timeFormatter = new Intl.DateTimeFormat(displayLocale.value, {
  hour: '2-digit',
  minute: '2-digit',
})

const displayName = computed(() => {
  const s = props.message.sender
  if (!s) return ''
  return `${s.firstname || ''} ${s.lastname || ''}`.trim()
})

const replyName = computed(() => {
  const s = props.message.replyTo?.sender
  if (!s) return ''
  return `${s.firstname || ''} ${s.lastname || ''}`.trim()
})

const stripHtml = (value?: string | null) =>
  String(value || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()

const replyPreview = computed(() => {
  if (!props.message.replyTo) return ''
  if (props.message.replyTo.deletedAt) return t('modules.chat.messageDeleted')
  return stripHtml(props.message.replyTo.body)
})

const timeLabel = computed(() => {
  const d = props.message._created instanceof Date
    ? props.message._created
    : new Date(props.message._created)
  return timeFormatter.format(d)
})

const linkifyUrls = (content: string) => {
  if (!content || /<a\s/i.test(content)) return content
  return content.replace(URL_PATTERN, (rawUrl) => {
    const cleaned = rawUrl.replace(/[.,;:!?)\]}>]+$/g, '')
    const trailing = rawUrl.slice(cleaned.length)
    return `<a href="${cleaned}" target="_blank" rel="noopener noreferrer nofollow">${cleaned}</a>${trailing}`
  })
}

const isUsablePictureUrl = (value?: string | null) =>
  !!value && (/^https?:\/\//i.test(value) || value.startsWith('/local_bucket/'))

/** Mentions as Vue segments + ResourceImage (signed URL + @error fallback). Never bake img into v-html. */
const bodySegments = computed((): BodySegment[] => {
  const body = props.message.body || ''
  if (!body || typeof DOMParser === 'undefined') {
    return body ? [{ kind: 'html', html: linkifyUrls(body) }] : []
  }

  const byId = new Map(
    (props.message.mentions || []).map(person => [String(person._id), person]),
  )
  const mentionMeta: Array<Extract<BodySegment, { kind: 'mention' }>> = []

  const doc = new DOMParser().parseFromString(`<div id="__chat_root">${body}</div>`, 'text/html')
  const root = doc.getElementById('__chat_root')
  if (!root) return [{ kind: 'html', html: linkifyUrls(body) }]

  root.querySelectorAll('span.mention, span[data-type="mention"]').forEach((node) => {
    const id = String(node.getAttribute('data-id') || '')
    const person = id ? byId.get(id) : null
    const firstname = person?.firstname || node.getAttribute('data-firstname') || ''
    const lastname = person?.lastname || node.getAttribute('data-lastname') || ''
    const fallback = (node.textContent || '').replace(/^@/, '').trim()
    const fullName = `${firstname} ${lastname}`.trim() || fallback || '—'
    const isMe = !!id && id === myResourceId.value
    const index = mentionMeta.length
    mentionMeta.push({
      kind: 'mention',
      id,
      isMe,
      label: isMe ? t('modules.chat.mentionYou') : fullName,
      resource: {
        _id: id || `unknown-${index}`,
        firstname,
        lastname,
        picture: isUsablePictureUrl(person?.picture) ? person!.picture : null,
      },
    })
    node.replaceWith(doc.createTextNode(`[[BC_MENTION:${index}]]`))
  })

  const html = linkifyUrls(root.innerHTML)
  const segments: BodySegment[] = []
  let lastIndex = 0
  for (const match of html.matchAll(MENTION_TOKEN)) {
    const start = match.index ?? 0
    if (start > lastIndex) {
      segments.push({ kind: 'html', html: html.slice(lastIndex, start) })
    }
    const mention = mentionMeta[Number(match[1])]
    if (mention) segments.push(mention)
    lastIndex = start + match[0].length
  }
  if (lastIndex < html.length) {
    segments.push({ kind: 'html', html: html.slice(lastIndex) })
  }

  return segments.length ? segments : [{ kind: 'html', html }]
})

const onReact = (emoji: string) => {
  emits('react', props.message, emoji)
  showReactionPicker.value = false
}
</script>

<template>
  <div
    class="group flex gap-2 px-3 py-1.5"
    :class="[
      isMine ? 'flex-row-reverse' : '',
      isMentioned && !message.deletedAt ? 'bg-amber-500/15 ring-1 ring-inset ring-amber-500/25 rounded-lg' : '',
    ]"
    :data-cy="`chat-message-${message._id}`"
    :data-message-type="message.type"
    :data-message-mine="isMine ? 'true' : 'false'"
  >
    <ResourceImage
      v-if="!isMine && message.sender"
      :resource="message.sender"
      width="w-8"
      height="h-8"
      class="shrink-0 mt-1"
      disable-link
    />
    <div
      v-else-if="!isMine"
      class="w-8 h-8 shrink-0 mt-1 rounded-full bg-primary-500/20"
    />
    <div class="max-w-[85%] min-w-0">
      <div
        class="rounded-2xl px-3 py-2 text-sm relative"
        :class="isMine
          ? 'bg-primary-500 text-white rounded-br-md'
          : 'bg-white dark:bg-white/10 border border-black/10 dark:border-white/10 rounded-bl-md shadow-xs'"
      >
        <div
          v-if="!isMine"
          class="text-xs font-medium opacity-70 mb-0.5"
        >
          {{ displayName }}
        </div>

        <div
          v-if="message.replyTo"
          class="mb-1.5 pl-2 border-l-2 text-xs opacity-80"
          :class="isMine ? 'border-white/50' : 'border-primary-500'"
        >
          <div class="font-medium truncate">
            {{ replyName || t('modules.chat.replyToMessage') }}
          </div>
          <div class="truncate italic">
            {{ replyPreview }}
          </div>
        </div>

        <p
          v-if="message.deletedAt"
          class="italic opacity-70"
          data-cy="chat-message-deleted"
        >
          {{ t('modules.chat.messageDeleted') }}
        </p>
        <template v-else>
          <img
            v-if="message.type === 'gif' && message.gif?.url"
            :src="message.gif.url"
            :alt="t('modules.chat.gif.title')"
            data-cy="chat-message-gif"
            class="rounded-lg max-w-full max-h-56 object-contain"
            loading="lazy"
          >
          <MessagePoll
            v-else-if="message.type === 'poll' && message.poll"
            :message="message"
            :tone="isMine ? 'on-primary' : 'default'"
            @updated="emits('updated', $event)"
          />
          <div
            v-else-if="bodySegments.length"
            class="chat-message-body whitespace-pre-wrap wrap-break-word [&_a]:underline [&_a]:break-all [&_a]:cursor-pointer"
            data-cy="chat-message-body"
            :class="[
              isMine
                ? 'chat-message-body--mine [&_a]:text-white [&_a]:decoration-white/70 hover:[&_a]:decoration-white'
                : '[&_a]:text-primary-500',
              isMentioned ? 'chat-message-body--mentioned' : '',
            ]"
          >
            <template
              v-for="(segment, index) in bodySegments"
              :key="index"
            >
              <span
                v-if="segment.kind === 'html'"
                v-html="segment.html"
              />
              <span
                v-else
                class="chat-mention"
                :class="segment.isMe ? 'chat-mention--me' : ''"
                contenteditable="false"
              >
                <ResourceImage
                  :resource="segment.resource"
                  width="w-[1.15rem]"
                  height="h-[1.15rem]"
                  shadow=""
                  :popover="false"
                  disable-link
                  class="chat-mention__avatar-wrap"
                />
                <span class="chat-mention__label">@{{ segment.label }}</span>
              </span>
            </template>
          </div>
          <MessageAttachment
            v-for="attachment in message.attachments || []"
            :key="attachment._id"
            :attachment="attachment"
            :tone="isMine ? 'on-primary' : 'default'"
          />
          <MessageEmbed
            v-for="embed in message.embeds || []"
            :key="`${embed.type}-${embed.entityId}`"
            :embed="embed"
            :tone="isMine ? 'on-primary' : 'default'"
          />
        </template>

        <div
          class="text-[10px] mt-1 opacity-60 flex items-center gap-1"
          :class="isMine ? 'justify-end' : ''"
        >
          <span>{{ timeLabel }}</span>
          <span
            v-if="message.editedAt"
            data-cy="chat-message-edited"
          > · {{ t('modules.chat.edited') }}</span>
          <span
            v-if="isMine && !message.deletedAt"
            class="inline-flex items-center gap-0.5"
            data-cy="chat-message-seen"
            :title="seenLabel"
          >
            <MaterialSymbolsDoneAllRounded
              v-if="isSeen"
              class="w-3.5 h-3.5"
            />
            <MaterialSymbolsCheckRounded
              v-else
              class="w-3.5 h-3.5"
            />
          </span>
        </div>
      </div>

      <div
        v-if="isMine && isSeen && seenBy.length"
        class="flex items-center gap-1 mt-1"
        :class="isMine ? 'justify-end' : ''"
      >
        <div class="flex -space-x-1 items-center">
          <ResourceImage
            v-for="person in seenBy.slice(0, 3)"
            :key="person._id"
            :resource="person"
            width="w-6"
            height="h-6"
            class="ring-1 ring-white/40 dark:ring-white/10 rounded-full"
            disable-link
          />
        </div>
        <span class="text-[11px] opacity-50 truncate max-w-40">
          {{ seenLabel }}
        </span>
      </div>

      <div
        v-if="message.reactions?.length"
        class="flex flex-wrap gap-1 mt-1"
        data-cy="chat-message-reactions"
        :class="isMine ? 'justify-end' : ''"
      >
        <button
          v-for="reaction in message.reactions"
          :key="reaction.emoji"
          type="button"
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs border transition-transform active:scale-95"
          :class="reaction.reactedByMe
            ? 'bg-primary-500/15 border-primary-500'
            : 'bg-white dark:bg-white/10 border-black/10 dark:border-white/10'"
          :data-cy="`chat-reaction-${reaction.emoji}`"
          @click="onReact(reaction.emoji)"
        >
          <span>{{ reaction.emoji }}</span>
          <span class="opacity-70">{{ reaction.count }}</span>
        </button>
      </div>

      <div
        v-if="!message.deletedAt"
        class="flex items-center gap-1 mt-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
        data-cy="chat-message-actions"
        :class="isMine ? 'justify-end' : ''"
      >
        <button
          type="button"
          class="p-1 rounded-sm bg-hover text-xs"
          data-cy="chat-message-reply"
          :title="t('modules.chat.reply')"
          @click="emits('reply', message)"
        >
          <MaterialSymbolsReplyRounded class="w-4 h-4" />
        </button>
        <div class="relative">
          <button
            type="button"
            class="p-1 rounded-sm bg-hover text-xs"
            data-cy="chat-message-react"
            :title="t('modules.chat.react')"
            @click="showReactionPicker = !showReactionPicker"
          >
            <MaterialSymbolsAddReactionOutlineRounded class="w-4 h-4" />
          </button>
          <div
            v-if="showReactionPicker"
            class="absolute z-20 bottom-full mb-1 flex gap-0.5 p-1 rounded-lg chat-glass border border-white/20 dark:border-white/10 shadow-lg"
            data-cy="chat-reaction-picker"
            :class="isMine ? 'right-0' : 'left-0'"
          >
            <button
              v-for="emoji in QUICK_EMOJIS"
              :key="emoji"
              type="button"
              class="w-7 h-7 rounded-sm bg-hover text-base active:scale-90 transition-transform"
              :data-cy="`chat-reaction-pick-${emoji}`"
              @click="onReact(emoji)"
            >
              {{ emoji }}
            </button>
          </div>
        </div>
        <template v-if="isMine && message.type === 'text'">
          <button
            type="button"
            class="p-1 rounded-sm bg-hover text-xs"
            data-cy="chat-message-edit"
            :title="t('modules.chat.edit')"
            @click="emits('edit', message)"
          >
            <MaterialSymbolsEditOutlineRounded class="w-4 h-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded-sm bg-hover text-xs text-error"
            data-cy="chat-message-delete"
            :title="t('modules.chat.delete')"
            @click="emits('delete', message)"
          >
            <MaterialSymbolsDeleteOutlineRounded class="w-4 h-4" />
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style>
.chat-mention {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  vertical-align: middle;
  margin: 0 0.1rem;
  padding: 0.05rem 0.4rem 0.05rem 0.15rem;
  border-radius: 999px;
  background: rgb(220 38 127 / 0.14);
  color: inherit;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
}

.chat-mention__avatar-wrap {
  flex-shrink: 0;
  font-size: 0.55rem;
}

.chat-mention__label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-mention--me {
  background: rgb(245 158 11 / 0.28);
  box-shadow: inset 0 0 0 1px rgb(245 158 11 / 0.45);
  color: rgb(146 64 14);
}

.dark .chat-mention--me {
  background: rgb(245 158 11 / 0.22);
  color: rgb(253 230 138);
}

.chat-message-body--mine .chat-mention:not(.chat-mention--me) {
  background: rgb(255 255 255 / 0.22);
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.25);
}

.chat-message-body--mine .chat-mention--me {
  background: rgb(255 255 255 / 0.95);
  color: rgb(146 64 14);
  box-shadow: none;
}

</style>
